// AutomationReadService.js

import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';
import {
  mergeSystemAndBaseAutomations,
  subscribeToTypeAutomations,
} from 'services/automations/TypeAutomationService';

const AUTOMATIONS_COLLECTION = collections.automations;
const sharedAutomationSubscriptions = new Map();

const normalizeScopeValue = (value) => String(value || '').trim();

function buildAutomationScope({ baseKey, nodeType } = {}) {
  return {
    baseKey: normalizeScopeValue(baseKey),
    nodeType: normalizeScopeValue(nodeType),
  };
}

function buildAutomationScopeKey(scope) {
  return [
    `baseKey=${scope.baseKey || '*'}`,
    `nodeType=${scope.nodeType || '*'}`,
  ].join('|');
}

function buildAutomationConstraints(scope) {
  const constraints = [];
  if (scope.baseKey) constraints.push(where('baseKey', '==', scope.baseKey));
  if (scope.nodeType) constraints.push(where('nodeType', '==', scope.nodeType));
  constraints.push(where('scope', '==', 'NODE_TYPE'));
  return constraints;
}

function ensureSharedAutomationSubscription(scope) {
  const scopeKey = buildAutomationScopeKey(scope);
  const existing = sharedAutomationSubscriptions.get(scopeKey);
  if (existing) return existing;

  const entry = {
    scope,
    scopeKey,
    rows: [],
    listeners: new Set(),
    unsubscribe: null,
  };

  const constraints = buildAutomationConstraints(scope);
  const q = query(collection(db, AUTOMATIONS_COLLECTION), ...constraints);

  entry.unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      entry.rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() || {}),
      }));

      entry.listeners.forEach((listener) => {
        try {
          listener(entry.rows);
        } catch (error) {
          console.error('[AutomationReadService] shared listener error:', error);
        }
      });
    },
    (error) => {
      console.error('[AutomationReadService] subscribeToAutomationRules error:', error);
      entry.rows = [];
      entry.listeners.forEach((listener) => listener([]));
    }
  );

  sharedAutomationSubscriptions.set(scopeKey, entry);
  return entry;
}

function attachSharedListener(scope, callback) {
  const entry = ensureSharedAutomationSubscription(scope);
  entry.listeners.add(callback);
  callback(entry.rows);

  return () => {
    const current = sharedAutomationSubscriptions.get(entry.scopeKey);
    if (!current) return;

    current.listeners.delete(callback);
    if (current.listeners.size > 0) return;

    current.unsubscribe?.();
    sharedAutomationSubscriptions.delete(entry.scopeKey);
  };
}

/**
 * Subscribes to automation rules for a base (optionally filtered by node type).
 * When `nodeType` is set, merges live Admin system automations ahead of base rules.
 *
 * @param {{ baseKey?: string, nodeType?: string }} options
 * @param {function} callback
 * @returns {function(): void}
 */
export function subscribeToAutomationRules({ baseKey, nodeType } = {}, callback) {
  if (typeof callback !== 'function') {
    return () => { };
  }

  const scope = buildAutomationScope({ baseKey, nodeType });
  if (!scope.baseKey) {
    callback([]);
    return () => { };
  }

  if (!scope.nodeType) {
    return attachSharedListener(scope, callback);
  }

  let baseRows = [];
  let systemRows = [];
  let cancelled = false;

  const emit = () => {
    if (cancelled) return;
    callback(mergeSystemAndBaseAutomations(systemRows, baseRows));
  };

  const unsubscribeBase = attachSharedListener(scope, (rows) => {
    baseRows = Array.isArray(rows) ? rows : [];
    emit();
  });

  const unsubscribeSystem = subscribeToTypeAutomations(scope.nodeType, (rows) => {
    systemRows = Array.isArray(rows) ? rows : [];
    emit();
  });

  return () => {
    cancelled = true;
    unsubscribeBase();
    unsubscribeSystem();
  };
}

export const subscribeToAutomationRulesShared = subscribeToAutomationRules;
